import { BaseApiClient } from './base-client';

// ===== TYPES =====

export interface TeamTierInfo {
  name: string;
  description: string;
  maxConcurrentImages: number;
  maxConcurrentVideos: number;
  monthlySpending: number;
  nextTier: string | null;
  amountToNextTier: number;
  progressToNextTier: number;
}

export type TeamTier = 'tier1' | 'tier2' | 'tier3';

export interface Team {
  id: string;
  name: string;
  avatarUrl?: string;
  tokens: number;
  role: 'owner' | 'admin' | 'member';
  membersCount: number;
  joinedAt?: string;
  createdAt: string;
  updatedAt?: string;
  ownerId?: string;
  // Tier info
  tier?: TeamTier;
  tierInfo?: TeamTierInfo;
  maxConcurrentImages?: number;
  maxConcurrentVideos?: number;
  subscription?: {
    status: string;
    expiresAt?: string;
    period: string;
    seats: number;
    hasPendingRenewal?: boolean;
  };
}

export interface TeamMember {
  userId: string;
  firstName: string;
  lastName?: string;
  username?: string;
  photoUrl?: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  stats: {
    imagesGenerated: number;
    videosGenerated: number;
    chatMessagesSent: number;
    tokensSpent: number;
  };
}

export interface TeamFolderAccessMember {
  userId: string;
  firstName?: string;
  username?: string;
  photoUrl?: string;
}

export interface TeamFolder {
  id: string;
  name: string;
  folderType: 'image' | 'video' | 'mixed';
  visibility: 'private' | 'members' | 'shared';
  isOwn?: boolean;
  accessList?: TeamFolderAccessMember[];
  createdBy: {
    userId: string;
    firstName?: string;
    username?: string;
    photoUrl?: string;
  };
  imagesCount: number;
  videosCount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface TeamTokenTransaction {
  id: string;
  type: 'topup' | 'spend' | 'refund' | 'transfer';
  amount: number;
  balanceAfter: number;
  description?: string;
  metadata?: Record<string, any>;
  user: {
    firstName?: string;
    username?: string;
    photoUrl?: string;
  };
  createdAt: string;
}

export interface TeamStats {
  team: {
    id: string;
    name: string;
    tokens: number;
    membersCount: number;
  };
  totals: {
    totalImages: number;
    totalVideos: number;
    totalChatMessages: number;
    totalTokensSpent: number;
    activeMembers: number;
  };
  members: Array<{
    userId: string;
    firstName?: string;
    username?: string;
    photoUrl?: string;
    role: string;
    imagesGenerated: number;
    videosGenerated: number;
    chatMessagesSent: number;
    tokensSpent: number;
  }>;
}

export interface TeamInvite {
  inviteLink: string;
  telegramLink: string;
  code: string;
  expiresAt: string;
  expiresIn: number;
}

export interface TeamInviteInfo {
  teamId: string;
  teamName: string;
  teamAvatarUrl?: string;
  membersCount: number;
  invitedBy: {
    firstName?: string;
    lastName?: string;
    username?: string;
  };
  expiresAt: string;
}

// ===== TEAM CHATS TYPES =====

export interface TeamChatMember {
  userId: string;
  firstName: string;
  lastName?: string;
  username?: string;
  photoUrl?: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  chatsCount: number;
  lastChatActivity?: string;
  isCurrentUser: boolean;
}

export interface TeamChatMemberInfo {
  id: string;
  firstName: string;
  lastName?: string;
  username?: string;
  photoUrl?: string;
}

export interface TeamChatSession {
  id: string;
  name: string;
  is_secret: boolean;
  is_archived: boolean;
  is_shared: boolean;
  created_at: string;
  updated_at: string;
  last_message_at?: string;
  message_count: number;
  // Creator info for team chats
  creator?: TeamChatMemberInfo;
  isOwnChat?: boolean;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Teams API - methods for team management
 */
export class TeamsApi extends BaseApiClient {

  /**
   * Validates teamId before any API call.
   * Throws if teamId is falsy or not a valid UUID (catches "undefined" string).
   */
  private assertTeamId(teamId: string): void {
    if (!teamId || !UUID_REGEX.test(teamId)) {
      throw new Error(`[TeamsApi] Invalid teamId: "${teamId}"`);
    }
  }

  // ===== TEAM CRUD =====

  /**
   * Get all teams for the current user
   */
  async getTeams(): Promise<{ success: boolean; teams: Team[] }> {
    const response = await this.client.get('/api/teams');
    return response.data;
  }

  /**
   * Create a new team
   */
  async createTeam(name: string, avatarS3Key?: string, avatarUrl?: string): Promise<{ success: boolean; message: string; team: Team }> {
    const response = await this.client.post('/api/teams', { name, avatarS3Key, avatarUrl });
    return response.data;
  }

  /**
   * Get team details
   */
  async getTeam(teamId: string): Promise<{ success: boolean; team: Team }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}`);
    return response.data;
  }

  /**
   * Update team
   */
  async updateTeam(teamId: string, data: { name?: string; avatarS3Key?: string; avatarUrl?: string }): Promise<{ success: boolean; message: string; team: Team }> {
    this.assertTeamId(teamId);
    const response = await this.client.put(`/api/teams/${teamId}`, data);
    return response.data;
  }

  /**
   * Delete team
   */
  async deleteTeam(teamId: string): Promise<{ success: boolean; message: string }> {
    this.assertTeamId(teamId);
    const response = await this.client.delete(`/api/teams/${teamId}`);
    return response.data;
  }

  // ===== MEMBERS =====

  /**
   * Get team members
   */
  async getTeamMembers(teamId: string): Promise<{ success: boolean; members: TeamMember[] }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/members`);
    return response.data;
  }

  /**
   * Remove team member
   */
  async removeTeamMember(teamId: string, userId: string): Promise<{ success: boolean; message: string }> {
    this.assertTeamId(teamId);
    const response = await this.client.delete(`/api/teams/${teamId}/members/${userId}`);
    return response.data;
  }

  /**
   * Update member role
   */
  async updateMemberRole(teamId: string, userId: string, role: 'admin' | 'member'): Promise<{ success: boolean; message: string; member: { userId: string; role: string } }> {
    this.assertTeamId(teamId);
    const response = await this.client.put(`/api/teams/${teamId}/members/${userId}`, { role });
    return response.data;
  }

  /**
   * Leave team
   */
  async leaveTeam(teamId: string): Promise<{ success: boolean; message: string }> {
    this.assertTeamId(teamId);
    const response = await this.client.post(`/api/teams/${teamId}/members/leave`);
    return response.data;
  }

  // ===== TOKENS =====

  /**
   * Get token balance and transaction history
   */
  async getTeamTokens(teamId: string, options?: { limit?: number; offset?: number; type?: string }): Promise<{ success: boolean; balance: number; transactions: TeamTokenTransaction[] }> {
    this.assertTeamId(teamId);
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.type) params.append('type', options.type);
    
    const response = await this.client.get(`/api/teams/${teamId}/tokens?${params.toString()}`);
    return response.data;
  }

  /**
   * Create payment for team token topup (YooKassa or Stars)
   */
  async createTeamPayment(teamId: string, amount: number, method: 'yookassa' | 'stars'): Promise<{
    success: boolean;
    paymentUrl?: string;
    paymentId?: string;
    method?: string;
    starsAmount?: number;
    tokens?: number;
    invoiceTitle?: string;
    invoiceDescription?: string;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.post(`/api/teams/${teamId}/tokens/create-payment`, { amount, method });
    return response.data;
  }

  /**
   * Top up team tokens (after payment)
   */
  async topUpTeamTokens(teamId: string, amount: number, paymentId?: string, paymentMethod?: string): Promise<{ success: boolean; message: string; newBalance: number }> {
    this.assertTeamId(teamId);
    const response = await this.client.post(`/api/teams/${teamId}/tokens/topup`, { amount, paymentId, paymentMethod });
    return response.data;
  }

  /**
   * Get spending breakdown by user
   */
  async getTeamSpending(teamId: string): Promise<{ success: boolean; spending: Array<{ userId: string; firstName?: string; username?: string; photoUrl?: string; totalSpent: number; transactionCount: number }> }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/tokens/spending`);
    return response.data;
  }

  // ===== FOLDERS =====

  /**
   * Get team folders
   */
  async getTeamFolders(teamId: string, type?: 'image' | 'video' | 'mixed'): Promise<{ success: boolean; folders: TeamFolder[] }> {
    this.assertTeamId(teamId);
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    params.append('_t', Date.now().toString()); // cache-bust
    const response = await this.client.get(`/api/teams/${teamId}/folders?${params.toString()}`);
    return response.data;
  }

  /**
   * Create team folder
   */
  async createTeamFolder(teamId: string, name: string, folderType: 'image' | 'video' | 'mixed' = 'image'): Promise<{ success: boolean; message: string; folder: TeamFolder }> {
    this.assertTeamId(teamId);
    const response = await this.client.post(`/api/teams/${teamId}/folders`, { name, folderType });
    return response.data;
  }

  /**
   * Update team folder
   */
  async updateTeamFolder(teamId: string, folderId: string, data: { name?: string; folderType?: string }): Promise<{ success: boolean; message: string; folder: TeamFolder }> {
    this.assertTeamId(teamId);
    const response = await this.client.put(`/api/teams/${teamId}/folders/${folderId}`, data);
    return response.data;
  }

  /**
   * Delete team folder
   */
  async deleteTeamFolder(teamId: string, folderId: string): Promise<{ success: boolean; message: string }> {
    this.assertTeamId(teamId);
    const response = await this.client.delete(`/api/teams/${teamId}/folders/${folderId}`);
    return response.data;
  }

  /**
   * Update folder visibility and access list
   */
  async setTeamFolderVisibility(
    teamId: string,
    folderId: string,
    visibility: 'private' | 'members' | 'shared',
    memberIds?: string[]
  ): Promise<{ success: boolean; folder: TeamFolder }> {
    this.assertTeamId(teamId);
    const response = await this.client.patch(
      `/api/teams/${teamId}/folders/${folderId}/visibility`,
      { visibility, memberIds }
    );
    return response.data;
  }

  /**
   * Get folder access list
   */
  async getTeamFolderAccess(teamId: string, folderId: string): Promise<{ success: boolean; accessList: TeamFolderAccessMember[] }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/folders/${folderId}/access`);
    return response.data;
  }

  // ===== STATS =====

  /**
   * Get team statistics
   */
  async getTeamStats(teamId: string): Promise<{ success: boolean; stats: TeamStats }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/stats`);
    return response.data;
  }

  /**
   * Get current user's stats in team
   */
  async getMyTeamStats(teamId: string): Promise<{ success: boolean; stats: { imagesGenerated: number; videosGenerated: number; chatMessagesSent: number; tokensSpent: number } }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/stats/me`);
    return response.data;
  }

  /**
   * Get team usage stats (models, categories, periods) — same format as personal usage stats
   */
  async getUsageStats(teamId: string, from: string, to: string, category: string = 'all', memberId?: string): Promise<any> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/stats/usage`, {
      params: { from, to, category, ...(memberId ? { memberId } : {}) }
    });
    return response.data;
  }

  // ===== INVITES =====

  /**
   * Create team invite
   */
  async createTeamInvite(teamId: string, expiresInMinutes: number = 5): Promise<{ success: boolean } & TeamInvite> {
    this.assertTeamId(teamId);
    const response = await this.client.post(`/api/teams/${teamId}/invite`, { expiresInMinutes });
    return response.data;
  }

  /**
   * Get invite info (public, no membership required)
   */
  async getTeamInviteInfo(code: string): Promise<{ success: boolean; invite: TeamInviteInfo }> {
    const response = await this.client.get(`/api/teams/invite/${code}`);
    return response.data;
  }

  /**
   * Accept team invite
   */
  async acceptTeamInvite(code: string): Promise<{ success: boolean; message: string; team: { id: string; name: string; avatarUrl?: string } }> {
    const response = await this.client.post(`/api/teams/invite/${code}/accept`);
    return response.data;
  }

  /**
   * Get invite history
   */
  async getTeamInviteHistory(teamId: string, includeExpired: boolean = false): Promise<{ success: boolean; invites: Array<any> }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/invite/history?includeExpired=${includeExpired}`);
    return response.data;
  }

  // ===== TEAM CHATS =====
  // Two-level structure: members -> their team chats

  /**
   * Get team chat members (list of team members with their team chat counts)
   */
  async getTeamChatMembers(teamId: string): Promise<{
    success: boolean;
    members: TeamChatMember[];
    currentUserId: string;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/chats`);
    return response.data;
  }

  /**
   * Get team chat sessions for a specific member
   */
  async getTeamMemberSessions(teamId: string, userId: string): Promise<{
    success: boolean;
    sessions: TeamChatSession[];
    user: TeamChatMemberInfo;
    isOwnChats: boolean;
    canWrite: boolean;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/chats/${userId}/sessions`);
    return response.data;
  }

  /**
   * Toggle chat session sharing with team
   */
  async toggleChatShare(teamId: string, sessionId: string, shared: boolean): Promise<{
    success: boolean;
    sessionId: string;
    is_shared: boolean;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.patch(`/api/teams/${teamId}/chats/sessions/${sessionId}/share`, { shared });
    return response.data;
  }

  /**
   * Get archived team chats for current user
   */
  async getTeamArchivedChats(teamId: string, userId: string): Promise<{
    success: boolean;
    sessions: TeamChatSession[];
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/chats/${userId}/sessions/archived`);
    return response.data;
  }

  /**
   * Get team chat history
   */
  async getTeamChatHistory(teamId: string, userId: string, sessionId: string, limit: number = 100): Promise<{
    success: boolean;
    messages: any[];
    count: number;
    session: { id: string; name: string; is_secret: boolean };
    user: TeamChatMemberInfo;
    isOwnChat: boolean;
    canWrite: boolean;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.get(`/api/teams/${teamId}/chats/${userId}/sessions/${sessionId}/history?limit=${limit}`);
    return response.data;
  }

  /**
   * Create a new team chat
   */
  async createTeamChat(teamId: string, name: string = 'New Chat', isSecret: boolean = false, password?: string): Promise<{
    success: boolean;
    session: TeamChatSession;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.post(`/api/teams/${teamId}/chats`, { name, isSecret, password });
    return response.data;
  }

  /**
   * Rename team chat (only creator)
   */
  async renameTeamChat(teamId: string, sessionId: string, name: string): Promise<{
    success: boolean;
    session: { id: string; name: string; updated_at: string };
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.patch(`/api/teams/${teamId}/chats/${sessionId}`, { name });
    return response.data;
  }

  /**
   * Archive/unarchive team chat (only creator)
   */
  async archiveTeamChat(teamId: string, sessionId: string, archived: boolean = true): Promise<{
    success: boolean;
    session: { id: string; is_archived: boolean; updated_at: string };
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.patch(`/api/teams/${teamId}/chats/${sessionId}/archive`, { archived });
    return response.data;
  }

  /**
   * Delete team chat (only creator)
   */
  async deleteTeamChat(teamId: string, sessionId: string): Promise<{
    success: boolean;
    deletedMessagesCount: number;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.delete(`/api/teams/${teamId}/chats/${sessionId}`);
    return response.data;
  }

  /**
   * Verify secret team chat password
   */
  async verifyTeamSecretChat(teamId: string, sessionId: string, password: string): Promise<{
    success: boolean;
    valid: boolean;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.post(`/api/teams/${teamId}/chats/${sessionId}/verify`, { password });
    return response.data;
  }

  /**
   * Send typing indicator to team members
   */
  async sendTypingIndicator(teamId: string, sessionId: string, isTyping: boolean): Promise<{
    success: boolean;
  }> {
    this.assertTeamId(teamId);
    const response = await this.client.post(`/api/teams/${teamId}/chats/typing`, { sessionId, isTyping });
    return response.data;
  }
}

